import React, { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import * as styles from "./rule-table-row.module.scss";
import { VelavuRule, VelavuRuleCategory } from "velavu-js-api";
import { mapRuleCategory } from "../../helper/language-helper";
import { DateTime } from "luxon";
import VelavuSwitch from "../../elements/velavu-switch";
import { classArr } from "../../helper/style-helper";
import IconMoreHorizontal from "../../dynamicicons/icon-more-horizontal";
import VelavuIconButton from "../../elements/velavu-icon-button";
import VelavuMenu, { VelavuMenuItem } from "../../elements/velavu-menu";
import whitelabel from "../../whitelabel";
import { useHasPermission } from "../sign-in-context/sign-in-context";
import HoverTooltip from "../../elements/hover-tooltip";
import { doesRuleContainConfig } from "./rules-permission-helper";
import { getPermissionDeniedMessage } from "../../helper/permission-helper";

export interface RuleTableRowProps {
	rule: VelavuRule;
	onSetEnabled?: (enabled: boolean) => void;
	clickable?: boolean;
	onClick?: () => void;
	onDuplicate?: () => void;
	onDelete?: () => void;
}

export default function RuleTableRow(props: RuleTableRowProps) {
	const { t } = useTranslation();

	const hasWritePermission = useHasPermission("rules:write");
	const hasWriteConfigPermission = useHasPermission("rules:write-config");
	const hasDeletePermission = useHasPermission("rules:delete");

	//Format the last modified date
	const lastModifiedString = useMemo(() => {
		return DateTime.fromISO(props.rule.last_modified).toLocaleString(
			DateTime.DATE_MED,
		);
	}, [props.rule.last_modified]);

	//Overflow menu
	const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
	const openMenu = useCallback(
		(event: React.MouseEvent<HTMLElement>) => {
			event.stopPropagation();

			setMenuAnchor(event.currentTarget);
		},
		[setMenuAnchor],
	);
	const closeMenu = useCallback(() => {
		setMenuAnchor(null);
	}, [setMenuAnchor]);

	//User and system rules can be enabled or disabled, but managed rules cannot
	const isRuleToggleable =
		props.rule.category === VelavuRuleCategory.User ||
		props.rule.category === VelavuRuleCategory.System;

	const editPermission = doesRuleContainConfig(props.rule)
		? "rules:write-config"
		: "rules:write";

	const hasEditPermission = useMemo(() => {
		if (doesRuleContainConfig(props.rule)) {
			return hasWriteConfigPermission;
		} else {
			return hasWritePermission;
		}
	}, [props.rule, hasWritePermission, hasWriteConfigPermission]);

	const canToggleRule = isRuleToggleable && hasEditPermission;

	//Only user rules can be duplicated or deleted
	const isRuleDuplicatableDeletable =
		props.rule.category === VelavuRuleCategory.User;

	const canDeleteRule = isRuleDuplicatableDeletable && hasDeletePermission;

	const propsOnDuplicate = props.onDuplicate;
	const onDuplicate = useCallback(() => {
		closeMenu();
		propsOnDuplicate?.();
	}, [closeMenu, propsOnDuplicate]);

	const propsOnDelete = props.onDelete;
	const onDelete = useCallback(() => {
		closeMenu();
		propsOnDelete?.();
	}, [closeMenu, propsOnDelete]);

	return (
		<>
			<VelavuMenu
				open={!!menuAnchor}
				onClose={closeMenu}
				refPosition={menuAnchor}
				align="right"
			>
				<VelavuMenuItem
					onClick={onDuplicate}
					label={t("rules.duplicate")}
					disabled={!canToggleRule}
				/>
				<VelavuMenuItem
					onClick={onDelete}
					label={t("common.delete")}
					danger
					disabled={!canDeleteRule}
				/>
			</VelavuMenu>

			<tr
				className={classArr(props.clickable && styles.clickableRow)}
				onClick={props.onClick}
			>
				{/* Rule name */}
				<td
					className={classArr(
						styles.ruleName,
						props.clickable && styles.clickableRuleName,
					)}
				>
					{props.rule.name ?? t("rules.unnamed_rule")}
				</td>

				{/* Rule type */}
				<td>{mapRuleCategory(props.rule.category, t)}</td>

				{/* Last updated */}
				<td>{lastModifiedString}</td>

				{/* Enabled toggle */}
				<td>
					<HoverTooltip
						tooltip={
							canToggleRule
								? undefined
								: getPermissionDeniedMessage(editPermission, t)
						}
					>
						<VelavuSwitch
							toggled={props.rule.enabled}
							onChange={props.onSetEnabled}
							disabled={!canToggleRule}
						/>
					</HoverTooltip>
				</td>

				{/* Action overflow button */}
				<td>
					<VelavuIconButton
						onClick={openMenu}
						small
						disabled={!canToggleRule && !canDeleteRule}
						tooltip={
							canToggleRule || canDeleteRule
								? undefined
								: getPermissionDeniedMessage(editPermission, t)
						}
					>
						<IconMoreHorizontal
							color={whitelabel.palette.secondary700}
						/>
					</VelavuIconButton>
				</td>
			</tr>
		</>
	);
}
